import { useState, useEffect, useRef } from 'react'
import HarvestApiService from '../service/HarvestApi.jsx'

const useExportStatus = (jwt, pollingInterval, errorCallback) => {
  const [statusResults, setStatusResults] = useState({})

  const poll = useRef(null)
  const pollingComplete = useRef(false)
  const inFlight = useRef(false)

  const stopPolling = () => {
    if (poll.current) {
      clearInterval(poll.current)
      poll.current = null
    }
  }

  const finishRequest = () => {
    inFlight.current = false
  }

  const shouldIgnoreRequestResult = () => {
    if (!pollingComplete.current) {
      return false
    }

    finishRequest()
    return true
  }

  const completePolling = () => {
    pollingComplete.current = true
    stopPolling()
  }

  const getErrorStatus = (error) => {
    return error?.response?.status ?? error?.status
  }

  const areAllRequestsSuccessful = (results) => {
    const requests = results?.data

    if (!Array.isArray(requests) || requests.length === 0) {
      return false
    }

    return requests.every((request) => {
      const status = request?.attributes?.status
      return typeof status === 'string' && status.toLowerCase() === 'success'
    })
  }

  const handleBatchFileRequestSuccess = (results) => {
    if (shouldIgnoreRequestResult()) {
      return
    }

    setStatusResults(results.data)

    if (areAllRequestsSuccessful(results.data)) {
      completePolling()
    }

    finishRequest()
  }

  const handleBatchFileRequestError = (error) => {
    if (shouldIgnoreRequestResult()) {
      return
    }

    onError(error)
    finishRequest()
  }

  const getBatchFileRequests = () => {
    if (pollingComplete.current || inFlight.current) {
      return
    }

    inFlight.current = true

    HarvestApiService.getBatchFileRequests(jwt, handleBatchFileRequestSuccess, handleBatchFileRequestError)
  }

  // call api on mount to fetch results right away
  useEffect(getBatchFileRequests, [])

  const onError = (error) => {
    const errorStatus = getErrorStatus(error)
    const errorMessage = error?.message || ''

    const isOffline = typeof navigator !== 'undefined' && navigator.onLine === false
    const isOnline = !isOffline
    const isGatewayTimeout = errorStatus === 504 || errorStatus === '504'
    const isNetworkError = errorMessage === 'Network Error'

    // Ignore 504 and online network errors, but still show popup when user is offline.
    if ((isGatewayTimeout && isOnline) || (isNetworkError && isOnline)) {
      return
    }

    stopPolling()
    if (typeof errorCallback === 'function') {
      errorCallback(error)
    }
  }

  // polling set up
  useEffect(() => {
    const startPolling = () => {
      if (pollingComplete.current) {
        return
      }

      const pollTimeout = setInterval(getBatchFileRequests, pollingInterval)
      poll.current = pollTimeout
    }

    // start the poll on mount
    startPolling()

    // cleanup on unmount: stop polling
    return () => {
      stopPolling()
    }
  }, [statusResults]) // eslint-disable-line react-hooks/exhaustive-deps
  // statusResults is the only dependency we care about in this useEffect;
  // no need to pass in additional objects in the dependency array

  return [statusResults]
}

export default useExportStatus
